// ============================================
// FAQ 知识中心 — 回收站页面
// ============================================

import { useEffect, useState } from 'react';
import { Trash2, RotateCcw, AlertTriangle, Loader2, Inbox, ChevronRight, Eye } from 'lucide-react';
import { useFaqStore } from './faq-store';
import FaqRichEditor from './FaqRichEditor';
import FaqHtmlSandboxPreview from './FaqHtmlSandboxPreview';
import { faqTagLabels } from './faq-tag-utils';

export default function FaqTrashPage() {
  const {
    trashItems, trashLoading,
    fetchTrashItems, fetchTags, restoreItem, hardDeleteItem, getPrimaryTags, getSecondaryTags,
  } = useFaqStore();

  const [hardDeleteConfirm, setHardDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    fetchTrashItems();
    fetchTags();
  }, [fetchTrashItems, fetchTags]);

  const allTags = [...getPrimaryTags(), ...getSecondaryTags()];

  async function handleRestore(id: string) {
    setActionLoading(id);
    try {
      await restoreItem(id);
    } catch (e) {
      console.error('Restore failed:', e);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleHardDelete(id: string) {
    setActionLoading(id);
    try {
      await hardDeleteItem(id);
      setHardDeleteConfirm(null);
    } catch (e) {
      console.error('Hard delete failed:', e);
    } finally {
      setActionLoading(null);
    }
  }

  function formatDeletedAt(dateStr: string | null): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* 标题 */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
            <Trash2 size={18} className="text-red-400" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">回收站</h1>
            <p className="text-xs text-slate-400">已删除的 FAQ 可在此恢复或彻底删除</p>
          </div>
          {trashItems.length > 0 && (
            <span className="ml-auto text-xs text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">
              共 {trashItems.length} 条
            </span>
          )}
        </div>

        {/* 加载中 */}
        {trashLoading && (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 size={20} className="animate-spin mr-2" />
            加载中...
          </div>
        )}

        {/* 空状态 */}
        {!trashLoading && trashItems.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <Inbox size={48} strokeWidth={1} className="mb-3 text-slate-300" />
            <p className="text-sm font-medium">回收站为空</p>
            <p className="text-xs mt-1">删除的 FAQ 会出现在这里</p>
          </div>
        )}

        {/* 列表 */}
        {!trashLoading && trashItems.length > 0 && (
          <div className="space-y-2">
            {trashItems.map((item) => {
              const isExpanded = expandedId === item.id;
              return (
              <div
                key={item.id}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 transition-all"
              >
                {/* 头部：可点击展开 */}
                <div
                  className="flex items-start gap-3 px-4 sm:px-5 py-3 sm:py-4 cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : item.id)}
                >
                  <button className="mt-0.5 text-slate-400 transition-transform" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)' }}>
                    <ChevronRight size={16} />
                  </button>
                  {/* 左侧内容 */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-700 truncate">
                      {item.question}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      {faqTagLabels(item, allTags, 1).map((tag) => (
                        <span key={tag} className="text-[10px] px-2 py-0.5 bg-sky-50 text-sky-600 rounded-full">
                          {tag}
                        </span>
                      ))}
                      {faqTagLabels(item, allTags, 2).map((tag) => (
                        <span key={tag} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">
                          {tag}
                        </span>
                      ))}
                      <span className="text-[10px] text-slate-400">
                        删除于 {formatDeletedAt(item.deleted_at)}
                      </span>
                    </div>
                  </div>

                  {/* 右侧操作 */}
                  <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleRestore(item.id)}
                      disabled={actionLoading === item.id}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-all"
                    >
                      {actionLoading === item.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <RotateCcw size={12} />
                      )}
                      恢复
                    </button>
                    <button
                      onClick={() => setHardDeleteConfirm(item.id)}
                      disabled={actionLoading === item.id}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-500 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-all"
                    >
                      <Trash2 size={12} />
                      <span className="hidden sm:inline">彻底删除</span>
                    </button>
                  </div>
                </div>

                {/* 展开预览内容 */}
                {isExpanded && (
                  <div className="border-t border-slate-100 px-4 sm:px-5 py-4 bg-slate-50/50">
                    <div className="flex items-center gap-2 mb-3">
                      <Eye size={14} className="text-slate-400" />
                      <span className="text-xs font-medium text-slate-400">内容预览（只读）</span>
                    </div>
                    {/* 回答内容 */}
                    {item.answer ? (
                      <div className="bg-white rounded-xl border border-slate-200 p-4 prose prose-sm max-w-none">
                        {item.answer_format === 'html' ? (
                          <FaqHtmlSandboxPreview html={item.answer} minHeight="520px" />
                        ) : (
                          <FaqRichEditor
                            content={item.answer}
                            readOnly
                            uploadImage={async () => ''}
                            minHeight="60px"
                          />
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">无回答内容</p>
                    )}
                    {/* 附件 */}
                    {item.attachments && item.attachments.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-slate-400 mb-1.5">附件 ({item.attachments.length})</p>
                        <div className="flex flex-wrap gap-2">
                          {item.attachments.map((att, i) => (
                            <span key={i} className="text-xs px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-slate-600 truncate max-w-[200px]">
                              {att.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* 备注 */}
                    {item.notes && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-slate-400 mb-1.5">备注</p>
                        <div className="bg-white rounded-xl border border-slate-200 p-3 prose prose-sm max-w-none">
                          <FaqRichEditor
                            content={item.notes}
                            readOnly
                            uploadImage={async () => ''}
                            minHeight="40px"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 彻底删除确认弹窗 */}
      {hardDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-[360px] max-w-[90vw]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle size={18} className="text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-800">彻底删除</h3>
            </div>
            <p className="text-sm text-slate-600 mb-1">
              此操作不可撤销，FAQ 将被永久删除。
            </p>
            <p className="text-sm font-medium text-slate-800 bg-red-50 rounded-lg px-3 py-2 mb-5 line-clamp-2">
              「{trashItems.find(i => i.id === hardDeleteConfirm)?.question}」
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setHardDeleteConfirm(null)}
                disabled={actionLoading === hardDeleteConfirm}
                className="px-4 py-2 text-sm text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-all"
              >取消</button>
              <button
                onClick={() => handleHardDelete(hardDeleteConfirm)}
                disabled={actionLoading === hardDeleteConfirm}
                className="px-4 py-2 text-sm text-white bg-red-500 rounded-xl hover:bg-red-600 disabled:opacity-50 transition-all flex items-center gap-1.5"
              >
                {actionLoading === hardDeleteConfirm && <Loader2 size={14} className="animate-spin" />}
                永久删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
